import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * How long to stop speculative prefetching after Shopify rate-limits us.
 * Shared across every card on the page: the limit is per shopper, not per
 * component, so one card learning about it has to stop all of them.
 */
const PREFETCH_COOLDOWN = 60_000;
let prefetchPausedUntil = 0;

const prefetchPaused = () => Date.now() < prefetchPausedUntil;

export class QuickAddComponent extends Component {
  /**
   * One controller per URL. A single shared controller meant every new hover
   * aborted the request the previous hover had started, which is most of the
   * "signal is aborted without reason" volume in Noibu #8.
   * @type {Map<string, AbortController>}
   */
  #abortControllers = new Map();
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingGrids = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // Start fetching on press rather than waiting for the click. It buys back
    // the gap between the two, and unlike the hover prefetch it works on touch,
    // where there is no hover to warm the cache and every click was cold. One
    // request per press, and loadProductGrid dedupes it against the click's own
    // call, so this cannot stampede the way a hover prefetch can.
    this.addEventListener('pointerdown', this.#warmProductGrid);
  }

  #warmProductGrid = () => {
    this.loadProductGrid(this.productPageUrl);
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('pointerdown', this.#warmProductGrid);
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    for (const controller of this.#abortControllers.values()) controller.abort();
    this.#abortControllers.clear();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    // Drop in-flight requests too: one that started before the cart changed
    // would otherwise resolve and repopulate the cache with pre-update content.
    this.#pendingGrids.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Resolves the product page content for a URL, caching it and sharing a single
   * request between the hover prefetch and the click.
   *
   * Without this the prefetch was wasted work: fetchProductPage only returns a
   * document, nothing stored it, and because it aborts its own previous call the
   * click cancelled the very prefetch it was about to wait for and refetched
   * from scratch. The product page is sent with no-store, so there is no HTTP
   * cache to fall back on either.
   *
   * @param {string} url - The product page URL
   * @returns {Promise<Element | null>} The product grid content, or null
   */
  loadProductGrid(url, { prefetch = false } = {}) {
    if (!url) return Promise.resolve(null);

    const cached = this.#cachedContent.get(url);
    if (cached) return Promise.resolve(cached);

    const inFlight = this.#pendingGrids.get(url);
    if (inFlight) return inFlight;

    /*
     * Shopify is rate-limiting us, so stop guessing. Each of these fetches is
     * a whole product page — 383KB on the Christmas cards collection — asked
     * for on the chance the shopper opens quick add, and piling more of them
     * onto a 429 helps nobody (Noibu #2). Only the speculative hover path is
     * held back: a press or a click still fetches, so nothing the shopper
     * actually asks for is refused.
     */
    if (prefetch && prefetchPaused()) return Promise.resolve(null);

    const request = this.fetchProductPage(url)
      .then((html) => {
        const gridElement = html?.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;

        // Cache the cloned element to avoid modifying the original
        const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(url, productGrid);
        return productGrid;
      })
      .catch(() => null)
      .finally(() => this.#pendingGrids.delete(url));

    this.#pendingGrids.set(url, request);
    return request;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // The dialog only opens once the product page has been fetched, and on a
    // cold click that is a whole round trip with nothing on screen to show for
    // it — which reads as the first click doing nothing, so the shopper presses
    // again. Mark the button busy up front so every press has an answer, and
    // ignore a second press while the first is still in flight.
    const target = /** @type {Element | null} */ (event.target);
    const button = target?.closest?.('.quick-add__button') ?? null;
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    try {
      const productGrid = await this.loadProductGrid(currentUrl);

      if (!productGrid) {
        /*
         * The fetch failed — a 404, a 429, a dropped connection. The dialog
         * used to open regardless, so the shopper got an empty box and no way
         * to tell what went wrong. Send them to the product page instead,
         * which is where they were headed, and which is what the selling-plans
         * branch above already does.
         */
        if (currentUrl) window.location.href = currentUrl;
        return;
      }

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      this.#openQuickAddModal();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncHeaderState(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Mirrors the header states that product media and details styles depend on. Those rules reach page
   * content from `#header-group` with a sibling combinator, which can't reach this body-level dialog.
   * Scroll is locked while the dialog is open, so the header can't change state until it closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #syncHeaderState(dialogComponent) {
    dialogComponent.toggleAttribute(
      'header-transparent',
      document.querySelector('header-component[transparent]') !== null
    );
    dialogComponent.toggleAttribute(
      'header-sticky-active',
      document.querySelector("#header-group #header-component[data-sticky-state='active']") !== null
    );
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches one URL and parses it, without owning the abort controller.
   *
   * @param {string} url
   * @param {AbortSignal} signal
   * @returns {Promise<Document | null>}
   */
  async #fetchDocument(url, signal) {
    const response = await fetch(url, { signal });

    if (response.status === 429) {
      prefetchPausedUntil = Date.now() + PREFETCH_COOLDOWN;
    }

    if (!response.ok) {
      // A prefetch that 404s or 429s is not an application error: the shopper
      // has not asked for anything yet. Throwing here escaped as an unhandled
      // rejection, because the pointerenter prefetch in product-card.js never
      // awaits this promise (Noibu #47). Return null and let the click fall
      // through to the product page link.
      return null;
    }

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // Abort only a duplicate in-flight request for this same URL.
    this.#abortControllers.get(productPageUrl)?.abort();
    const controller = new AbortController();
    this.#abortControllers.set(productPageUrl, controller);

    try {
      /*
       * The whole product page, which is heavy — 383KB — for the one node the
       * modal reads. Narrowing it to ?section_id=main looked right (every
       * product template names that section 'main') but returns 404 on this
       * storefront for every product, so it is not available here. Horizon
       * does its section rendering through dedicated section FILES instead:
       * see SECTION_ID_MAP in variant-picker.js, which requests
       * 'section-rendering-product-card'. Any retry of this idea has to go
       * that way, and has to be checked against the live storefront first.
       */
      return await this.#fetchDocument(productPageUrl, controller.signal);
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortControllers.get(productPageUrl) === controller) {
        this.#abortControllers.delete(productPageUrl);
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      // The product's accordion (Product Details, Shipping + Returns,
      // #CraftChange) lives inside .product-details, which is removed below to
      // build the compact sheet. Taken out first and re-appended, otherwise the
      // description is gone on mobile with no way to read what the product is —
      // snippets/quick-add-modal.liquid collapses these rows so the sheet stays
      // compact and the description sits behind a line the shopper can tap.
      const accordion = productDetails?.querySelector('.accordion');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (accordion) {
        productGrid.appendChild(accordion);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
